/**
 * Officer teamwork over gov-api: file notes with @mentions, following files,
 * shift handovers, the unified inbox, the work-summary digest and the private
 * calendar link.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AcknowledgeShiftHandoverDto,
  CalendarFeedCreatedDto,
  CalendarFeedStatusDto,
  CreateFileNoteDto,
  CreateShiftHandoverDto,
  FileNoteDto,
  FileWatchStateDto,
  ShiftHandoverDto,
  ShiftHandoverListDto,
  TeamworkPersonDto,
  TeamworkPreferencesDto,
  UpdateTeamworkPreferencesDto,
  WatchedFileDto,
} from '@munifocus/contracts-gov';
import type {
  InboxTabId,
  InboxUnreadCountsDto,
  NotificationInboxDto,
} from '@munifocus/contracts-common';
import { api } from './api-client';
import { notificationKeys } from './notifications-api';

export const teamworkKeys = {
  notes: (fileId: string) => ['teamwork', 'notes', fileId] as const,
  mentionable: (fileId: string) => ['teamwork', 'mentionable', fileId] as const,
  watch: (fileId: string) => ['teamwork', 'watch', fileId] as const,
  watching: ['teamwork', 'watching'] as const,
  fileHandovers: (fileId: string) => ['teamwork', 'file-handovers', fileId] as const,
  handovers: ['teamwork', 'handovers'] as const,
  handover: (id: string) => ['teamwork', 'handover', id] as const,
  candidates: (fileIds: string[], lg: string | null) =>
    ['teamwork', 'candidates', fileIds.join(','), lg ?? ''] as const,
  inbox: (tab: InboxTabId, unreadOnly: boolean) =>
    [...notificationKeys.inbox, 'tab', tab, unreadOnly] as const,
  unreadCounts: [...notificationKeys.inbox, 'unread-counts'] as const,
  preferences: ['teamwork', 'preferences'] as const,
  calendar: ['teamwork', 'calendar-feed'] as const,
};

// --- Notes ----------------------------------------------------------------

export function useFileNotes(fileId: string) {
  return useQuery({
    queryKey: teamworkKeys.notes(fileId),
    queryFn: async () => (await api.get<FileNoteDto[]>(`/files/${fileId}/notes`)).data,
  });
}

export function useMentionable(fileId: string, enabled = true) {
  return useQuery({
    queryKey: teamworkKeys.mentionable(fileId),
    queryFn: async () => (await api.get<TeamworkPersonDto[]>(`/files/${fileId}/mentionable`)).data,
    enabled: Boolean(fileId) && enabled,
    staleTime: 5 * 60_000,
  });
}

export function useCreateNote(fileId: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { silent: true },
    mutationFn: async (dto: CreateFileNoteDto) =>
      (await api.post<FileNoteDto>(`/files/${fileId}/notes`, dto)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: teamworkKeys.notes(fileId) }),
  });
}

export function useUpdateNote(fileId: string) {
  const qc = useQueryClient();
  return useMutation({
    meta: { silent: true },
    mutationFn: async ({ id, ...dto }: CreateFileNoteDto & { id: string }) =>
      (await api.patch<FileNoteDto>(`/files/${fileId}/notes/${id}`, dto)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: teamworkKeys.notes(fileId) }),
  });
}

export function useDeleteNote(fileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/files/${fileId}/notes/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: teamworkKeys.notes(fileId) }),
  });
}

// --- Watching -------------------------------------------------------------

export function useWatchState(fileId: string) {
  return useQuery({
    queryKey: teamworkKeys.watch(fileId),
    queryFn: async () => (await api.get<FileWatchStateDto>(`/files/${fileId}/watch`)).data,
  });
}

export function useSetWatching(fileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (watching: boolean) =>
      (watching
        ? await api.put<FileWatchStateDto>(`/files/${fileId}/watch`)
        : await api.delete<FileWatchStateDto>(`/files/${fileId}/watch`)
      ).data,
    onSuccess: (state) => {
      qc.setQueryData(teamworkKeys.watch(fileId), state);
      qc.invalidateQueries({ queryKey: teamworkKeys.watching });
    },
  });
}

export function useWatching() {
  return useQuery({
    queryKey: teamworkKeys.watching,
    queryFn: async () => (await api.get<WatchedFileDto[]>('/me/watching')).data,
  });
}

// --- Handovers ------------------------------------------------------------

export function useHandovers() {
  return useQuery({
    queryKey: teamworkKeys.handovers,
    queryFn: async () => (await api.get<ShiftHandoverListDto>('/handovers')).data,
    refetchInterval: 5 * 60_000,
  });
}

export function useHandover(id: string) {
  return useQuery({
    queryKey: teamworkKeys.handover(id),
    queryFn: async () => (await api.get<ShiftHandoverDto>(`/handovers/${id}`)).data,
    enabled: Boolean(id),
  });
}

export function useFileHandovers(fileId: string) {
  return useQuery({
    queryKey: teamworkKeys.fileHandovers(fileId),
    queryFn: async () => (await api.get<ShiftHandoverDto[]>(`/files/${fileId}/handovers`)).data,
  });
}

export function useHandoverCandidates(fileIds: string[], localGovernmentId: string | null) {
  return useQuery({
    queryKey: teamworkKeys.candidates(fileIds, localGovernmentId),
    queryFn: async () =>
      (
        await api.get<TeamworkPersonDto[]>('/handovers/candidates', {
          params: {
            ...(fileIds.length ? { fileIds: fileIds.join(',') } : {}),
            ...(localGovernmentId && !fileIds.length ? { localGovernmentId } : {}),
          },
        })
      ).data,
    enabled: fileIds.length > 0 || Boolean(localGovernmentId),
    placeholderData: keepPreviousData,
  });
}

function invalidateHandovers(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['teamwork', 'handovers'] });
  qc.invalidateQueries({ queryKey: ['teamwork', 'handover'] });
  qc.invalidateQueries({ queryKey: ['teamwork', 'file-handovers'] });
}

export function useCreateHandover() {
  const qc = useQueryClient();
  return useMutation({
    meta: { silent: true },
    mutationFn: async (dto: CreateShiftHandoverDto) =>
      (await api.post<ShiftHandoverDto>('/handovers', dto)).data,
    onSuccess: () => invalidateHandovers(qc),
  });
}

export function useAcknowledgeHandover() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...dto }: AcknowledgeShiftHandoverDto & { id: string }) =>
      (await api.post<ShiftHandoverDto>(`/handovers/${id}/acknowledge`, dto)).data,
    onSuccess: () => invalidateHandovers(qc),
  });
}

export function useCancelHandover() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<ShiftHandoverDto>(`/handovers/${id}/cancel`)).data,
    onSuccess: () => invalidateHandovers(qc),
  });
}

// --- Inbox ----------------------------------------------------------------

export function useInboxTab(tab: InboxTabId, unreadOnly: boolean) {
  return useQuery({
    queryKey: teamworkKeys.inbox(tab, unreadOnly),
    queryFn: async () =>
      (
        await api.get<NotificationInboxDto>('/notifications/inbox', {
          params: { tab, limit: 100, ...(unreadOnly ? { unread: 'true' } : {}) },
        })
      ).data,
    placeholderData: keepPreviousData,
  });
}

export function useUnreadCounts() {
  return useQuery({
    queryKey: teamworkKeys.unreadCounts,
    queryFn: async () =>
      (await api.get<InboxUnreadCountsDto>('/notifications/inbox/unread-counts')).data,
    refetchInterval: 60_000,
  });
}

/** Read or unread, one row. Everything under the inbox prefix refetches. */
export function useSetRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, read }: { id: string; read: boolean }) => {
      await api.post(`/notifications/inbox/${id}/${read ? 'read' : 'unread'}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.inbox }),
  });
}

// --- Work summary + calendar ---------------------------------------------

export function useTeamworkPreferences() {
  return useQuery({
    queryKey: teamworkKeys.preferences,
    queryFn: async () => (await api.get<TeamworkPreferencesDto>('/me/teamwork-preferences')).data,
  });
}

export function useUpdateTeamworkPreferences() {
  const qc = useQueryClient();
  return useMutation({
    meta: { silent: true },
    mutationFn: async (dto: UpdateTeamworkPreferencesDto) =>
      (await api.put<TeamworkPreferencesDto>('/me/teamwork-preferences', dto)).data,
    onSuccess: (data) => qc.setQueryData(teamworkKeys.preferences, data),
  });
}

export function useCalendarFeed() {
  return useQuery({
    queryKey: teamworkKeys.calendar,
    queryFn: async () => (await api.get<CalendarFeedStatusDto>('/me/calendar-feed')).data,
  });
}

export function useCreateCalendarFeed() {
  const qc = useQueryClient();
  return useMutation({
    meta: { silent: true },
    mutationFn: async () => (await api.post<CalendarFeedCreatedDto>('/me/calendar-feed')).data,
    onSuccess: (data) =>
      qc.setQueryData<CalendarFeedStatusDto>(teamworkKeys.calendar, {
        active: data.active,
        createdAt: data.createdAt,
        lastUsedAt: data.lastUsedAt,
      }),
  });
}

export function useRevokeCalendarFeed() {
  const qc = useQueryClient();
  return useMutation({
    meta: { silent: true },
    mutationFn: async () => (await api.delete<CalendarFeedStatusDto>('/me/calendar-feed')).data,
    onSuccess: (data) => qc.setQueryData(teamworkKeys.calendar, data),
  });
}

/**
 * The in-portal path a stored notification link points at, or null.
 *
 * Notifications store an absolute portal URL (the same one the email carries),
 * so the bell used to ignore them. Only paths this portal serves are followed;
 * anything else — an emailed form link on another site — is left alone.
 */
const IN_APP_PREFIXES = ['/files', '/handovers', '/inbox', '/messages', '/requests', '/settings'];

export function inPortalPath(linkUrl: string | null | undefined): string | null {
  if (!linkUrl) return null;
  let path: string;
  if (linkUrl.startsWith('/')) path = linkUrl;
  else {
    try {
      const url = new URL(linkUrl);
      path = `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  }
  if (
    path === '/' ||
    IN_APP_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}?`))
  ) {
    return path;
  }
  return null;
}
